/**
 * Morse — die Listen der Hörspur: was in welcher Reihenfolge klingt und was dazu zu lesen steht. (07.10.2026)
 *
 * Martin: „… so funktioniert die Übung auch mit einem Abspielbutton, dass man sich anhören kann, wie alles
 * funktioniert. Also eine Audio-Spur zum Anhören.“
 *
 * Rein: kein DOM, kein Ton, kein Speicher. Eine Liste ist DATEN — Kapitel aus Schritten —, damit der Abspieler
 * (morse-hoerspur.ts) nichts über Morse wissen muss und später je Kapitel eine Stimme dazukommen kann, ohne dass
 * sich die Kapitel ändern. Zwei Listen:
 *
 *   - „Vorführung“: Punkt und Strich → das neue Zeichen der Stufe (langsam, dann normal) → eine Übungsaufgabe →
 *     das Tempo (dasselbe Zeichen in drei Tempi). Bei den EIGENEN Einstellungen des Kontos: so klingt die Übung
 *     wirklich. Die Aufgabe ist deterministisch (fester Startwert) — immer dieselbe, darum testbar.
 *   - Tabelle: jedes Zeichen in der gewählten Ordnung, eins nach dem anderen.
 *
 * ⚠️ KEINE zweite Zeitrechnung: jeder Ton ist ein `zeitplan()` aus shared/morse.ts, „langsam“ ist
 *    `langsamesTempo()` (dieselbe Rechnung wie beim Kennenlernen), und die Dauer eines Schritts ist der Vorlauf
 *    des Spielers (`MORSE_VORLAUF_S`) plus `plan.gesamtMs` plus die Stille danach — die Zeit, die der Spieler
 *    wirklich braucht.
 */
import { kochAufgabe, kochNeueZeichen, kochStufe } from "../../shared/morse-koch.js";
import { MORSE_GRENZEN, MORSE_TABELLE, langsamesTempo, musterSilben, zeitplan, type MorseEinstellungen, type ZeitPlan } from "../../shared/morse.js";
import { HOERSPUR } from "./morse-texte.js";
import { MORSE_VORLAUF_S } from "./morse-ton.js";

/** Die Hörspur kennt zwei Listen. „demo“ steht auch in den Diagnosezeilen (ASCII, greppbar). */
type HoerListenArt = "demo" | "tabelle";

/** Ein Schritt: etwas klingt (`plan`) und/oder etwas steht zu lesen (`untertitel`), danach Stille. */
export interface HoerSchritt {
  /** Der Ton dieses Schritts — `null`: nur Text (Lesezeit). */
  plan: ZeitPlan | null;
  /** Was gespielt wird (Zeichen, Gruppe) — leer ohne Ton. */
  text: string;
  /** Die mitlaufende Textzeile. */
  untertitel: string;
  /** Zeichen, deren Muster als Bild zu sehen ist (und mit dem Ton leuchtet). */
  zeigen: string[];
  /** Mit dem Zeichen als Wort darüber? (Beim Punkt und Strich nicht — dort gibt es kein Zeichen.) */
  namen: boolean;
  /** Ihre Muster in Schrift („-.-“) — für den Test und die Anzeige „samt Muster“. */
  muster: string[];
  /** Die Tabellen-Zeichen: welche Karte gerade dran ist. */
  zeichen?: string;
  /** Stille nach dem Ton (oder die Lesezeit ohne Ton), ms. */
  nachMs: number;
  /** Dauer des ganzen Schritts: Vorlauf + Ton + Stille (ms). */
  ms: number;
  /** Beginn dieses Schritts ab Anfang der Liste (ms). */
  ab: number;
}

interface HoerKapitel {
  id: string;
  titel: string;
  schritte: HoerSchritt[];
  ms: number;
  ab: number;
}

export interface HoerListe {
  id: HoerListenArt;
  titel: string;
  /** Tonhöhe der ganzen Liste (Hz) — die eigene des Kontos. */
  ton: number;
  kapitel: HoerKapitel[];
  /** Gesamtdauer (ms). */
  ms: number;
  /** Anzahl der Schritte. */
  schritte: number;
  /** Wie der Fortschritt gesagt wird: als Zeit („0:35 von 2:05 Min.“) oder als Zahl („Zeichen 3 von 59“). */
  zaehlung: "zeit" | "zeichen";
}

/** So viel Lesezeit braucht eine Textzeile mindestens (ms), dazu je Zeichen etwas. */
const LESEZEIT_BASIS_MS = 3000;
const LESEZEIT_JE_ZEICHEN_MS = 80;
/** Nach einem Ton bleibt es mindestens so lange still (ms). */
export const NACH_MINDESTENS_MS = 700;
/** Die kurze Stille nach der Aufgabe, bevor das Tippen gezeigt wird (ms). */
export const AUFGABE_STILLE_MS = 2000;
/** Zwischen zwei Zeichen der Tabelle (ms). */
export const TABELLE_NACH_MS = 900;
/** Der feste Startwert der Aufgabe: immer dieselbe Gruppe. */
export const AUFGABE_STARTWERT = 7;

/** Lesezeit einer Zeile (ms). */
export function lesezeitMs(text: string): number {
  return LESEZEIT_BASIS_MS + [...text].length * LESEZEIT_JE_ZEICHEN_MS;
}

/** Ein Zufall mit festem Startwert (Lehmer, wie in den Tests): dieselbe Folge bei jedem Aufruf. */
export function festerZufall(startwert: number): () => number {
  let a = Math.min(2147483646, Math.max(1, Math.round(startwert)));
  return () => {
    a = (a * 16807) % 2147483647;
    return (a - 1) / 2147483646;
  };
}

type Tempo = Pick<MorseEinstellungen, "tempo" | "wirksam">;

interface SchrittWunsch {
  text?: string;
  tempo?: Tempo;
  untertitel: string;
  zeigen?: string[];
  namen?: boolean;
  zeichen?: string;
  nachMs?: number;
}

function baueSchritt(w: SchrittWunsch, e: Tempo): Omit<HoerSchritt, "ab"> {
  // „CH“ ist in der Tabelle EIN Zeichen (----); in einer Gruppe stehen C und H einzeln.
  const plan = w.text ? zeitplan(w.text, w.tempo ?? e, { ch: w.text === "CH" }) : null;
  const tonMs = plan ? MORSE_VORLAUF_S * 1000 + plan.gesamtMs : 0;
  const nachMs = w.nachMs ?? Math.max(NACH_MINDESTENS_MS, lesezeitMs(w.untertitel) - tonMs);
  const zeigen = w.zeigen ?? [];
  return {
    plan,
    text: w.text ?? "",
    untertitel: w.untertitel,
    zeigen,
    namen: w.namen ?? true,
    muster: zeigen.map((z) => MORSE_TABELLE[z] ?? ""),
    ...(w.zeichen !== undefined ? { zeichen: w.zeichen } : {}),
    nachMs,
    ms: tonMs + nachMs,
  };
}

/** Die Kapitel mit Zeiten versehen: Beginn jedes Schritts und jedes Kapitels, Dauer, Zahl der Schritte. */
function bauListe(id: HoerListenArt, titel: string, ton: number, zaehlung: HoerListe["zaehlung"], kapitel: Array<{ id: string; titel: string; schritte: Array<Omit<HoerSchritt, "ab">> }>): HoerListe {
  let t = 0;
  let anzahl = 0;
  const k = kapitel.map((kap) => {
    const ab = t;
    const schritte = kap.schritte.map((s) => {
      const mit: HoerSchritt = { ...s, ab: t };
      t += s.ms;
      anzahl++;
      return mit;
    });
    return { id: kap.id, titel: kap.titel, schritte, ms: t - ab, ab };
  });
  return { id, titel, ton, kapitel: k, ms: t, schritte: anzahl, zaehlung };
}

/** Das dritte Tempo der Vorführung: die Pausen nicht mehr gedehnt — oder, wo sie es schon sind, etwas schneller. */
export function schnellesTempo(e: Tempo): { tempo: number; wirksam: number; gedehnt: boolean } {
  if (e.wirksam < e.tempo) return { tempo: e.tempo, wirksam: e.tempo, gedehnt: true };
  const t = Math.min(MORSE_GRENZEN.tempoMax, e.tempo + 5);
  return { tempo: t, wirksam: t, gedehnt: false };
}

/**
 * Die Vorführung (ca. zwei bis drei Minuten) für diese Stufe und diese Einstellungen.
 * `e.ton` ist die Tonhöhe der Liste; Tempo und Pausen sind die des Kontos.
 */
export function vorfuehrung(stufe: number, e: MorseEinstellungen): HoerListe {
  const s = kochStufe(stufe);
  const neue = kochNeueZeichen(s);
  const langsam = langsamesTempo(e);
  const H = HOERSPUR;

  // 1 — Punkt und Strich: E ist ein Punkt, T ein Strich; je dreimal hintereinander (Pausen wie im Zeitplan).
  const punktStrich = [
    baueSchritt({ text: "EEE", untertitel: H.punktStrich.kurz, zeigen: ["E", "E", "E"], namen: false }, e),
    baueSchritt({ text: "TTT", untertitel: H.punktStrich.lang, zeigen: ["T", "T", "T"], namen: false }, e),
  ];

  // 2 — das neue Zeichen: vorgestellt mit den Silben, dann je Zeichen langsam und im richtigen Tempo.
  const silben = neue.map((z) => ({ zeichen: z, silben: musterSilben(MORSE_TABELLE[z] ?? "") }));
  const neuesZeichen = [
    baueSchritt({ untertitel: H.neu.vorstellung(s, silben), zeigen: neue }, e),
    ...silben.flatMap(({ zeichen, silben: sil }) => [
      baueSchritt({ text: zeichen, tempo: langsam, untertitel: H.neu.langsam(zeichen, sil), zeigen: [zeichen] }, e),
      baueSchritt({ text: zeichen, untertitel: H.neu.normal(zeichen, sil), zeigen: [zeichen] }, e),
    ]),
  ];

  // 3 — eine Übungsaufgabe: eine Gruppe aus fünf Zeichen der Stufe, kurze Stille, dann das Tippen samt Muster.
  const gruppe = kochAufgabe(s, { art: "gruppen", anzahl: 1, zufall: festerZufall(AUFGABE_STARTWERT) }).text;
  const zeichenDerGruppe = [...gruppe];
  const aufgabe = [
    baueSchritt({ untertitel: H.aufgabe.vorher }, e),
    baueSchritt({ text: gruppe, untertitel: H.aufgabe.hoere, namen: false, nachMs: AUFGABE_STILLE_MS }, e),
    baueSchritt({ untertitel: H.aufgabe.tippen(zeichenDerGruppe.join(" ")), zeigen: zeichenDerGruppe }, e),
    baueSchritt({ untertitel: H.aufgabe.danach }, e),
  ];

  // 4 — das Tempo: dasselbe Zeichen dreimal, in drei Tempi; vorher der eine Satz, was es heißt.
  const z = neue[0];
  const dreimal = `${z}${z}${z}`;
  const schnell = schnellesTempo(e);
  const tempo = [
    baueSchritt({ untertitel: H.tempo.satz }, e),
    baueSchritt({ text: dreimal, tempo: langsam, untertitel: H.tempo.langsam(langsam.tempo, langsam.wirksam), zeigen: [z, z, z] }, e),
    baueSchritt({ text: dreimal, untertitel: H.tempo.dein(e.tempo, e.wirksam), zeigen: [z, z, z] }, e),
    baueSchritt(
      {
        text: dreimal,
        tempo: schnell,
        untertitel: schnell.gedehnt ? H.tempo.ohneDehnung(schnell.tempo) : H.tempo.schneller(schnell.tempo),
        zeigen: [z, z, z],
      },
      e,
    ),
  ];

  return bauListe("demo", HOERSPUR.vorfuehrung.titel, e.ton, "zeit", [
    { id: "punkt-strich", titel: H.punktStrich.titel, schritte: punktStrich },
    { id: "neu", titel: H.neu.titel(neue.length), schritte: neuesZeichen },
    { id: "aufgabe", titel: H.aufgabe.titel, schritte: aufgabe },
    { id: "tempo", titel: H.tempo.titel, schritte: tempo },
  ]);
}

/**
 * Die Tabelle der Reihe nach: jedes Zeichen von `zeichen` (in der Ordnung, wie die Tabelle sie zeigt), mit den
 * Einstellungen des Kontos — so wie das Antippen. Ein Kapitel, ein Schritt je Zeichen. Die Karte der Hörspur zeigt
 * nur die Textzeile („K — dah-di-dah“): das Bild leuchtet in der Karte des Zeichens selbst, in der Tabelle.
 */
export function tabellenListe(o: { zeichen: readonly string[]; ordnung: string }, e: MorseEinstellungen): HoerListe {
  const schritte = o.zeichen
    .filter((z) => MORSE_TABELLE[z] !== undefined)
    .map((z) => baueSchritt({ text: z, untertitel: `${z} — ${musterSilben(MORSE_TABELLE[z])}`, zeichen: z, nachMs: TABELLE_NACH_MS }, e));
  return bauListe("tabelle", HOERSPUR.tabelle.titel(o.ordnung), e.ton, "zeichen", [{ id: "tabelle", titel: o.ordnung, schritte }]);
}
